import React, { useState } from 'react';
import {
  API_URL,
  DEFAULT_INDOOR_RATE_CENTS,
  DEFAULT_OUTDOOR_RATE_CENTS,
  getAuthToken,
  getVenueQuery
} from './internalIntegrationUtils';

interface Court {
  id: string;
  name: string;
  type: string;
  sport: string;
  isActive: boolean;
  hourlyRateCents: number;
}

interface LocationConfig {
  slug: string;
  name: string;
  courtTypes: { typeSlug: string; courtType: string; courtName: string; courtCount: number }[];
}

// All 13 CNP locations with their court type configs
const CNP_LOCATIONS: LocationConfig[] = [
  {
    slug: 'glendale-az', name: 'Glendale, AZ',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 3 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 6 },
    ]
  },
  {
    slug: 'thornton-co', name: 'Thornton, CO',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 6 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 2 },
    ]
  },
  {
    slug: 'overland-park', name: 'Overland Park, KS',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 5 },
      { typeSlug: 'pickleball-outdoor-court', courtType: 'outdoor', courtName: 'Outdoor Court', courtCount: 2 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 2 },
    ]
  },
  {
    slug: 'wichita', name: 'Wichita, KS',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 6 },
      { typeSlug: 'pickleball-outdoor-court', courtType: 'outdoor', courtName: 'Outdoor Court', courtCount: 5 },
    ]
  },
  {
    slug: 'north-kansas-city', name: 'North Kansas City, MO',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 4 },
      { typeSlug: 'pickleball-outdoor-court', courtType: 'outdoor', courtName: 'Outdoor Court', courtCount: 4 },
    ]
  },
  {
    slug: 'st-charles-mo', name: 'St. Charles, MO',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 3 },
      { typeSlug: 'pickleball-outdoor-court', courtType: 'outdoor', courtName: 'Outdoor Court', courtCount: 2 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 3 },
    ]
  },
  {
    slug: 'henderson-nv', name: 'Henderson, NV',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 1 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 7 },
    ]
  },
  {
    slug: 'oklahoma-city', name: 'Oklahoma City, OK',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 5 },
      { typeSlug: 'pickleball-outdoor-court', courtType: 'outdoor', courtName: 'Outdoor Court', courtCount: 4 },
    ]
  },
  {
    slug: 'allen-tx', name: 'Allen, TX',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 5 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 2 },
    ]
  },
  {
    slug: 'grand-prairie', name: 'Grand Prairie, TX',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 5 },
      { typeSlug: 'pickleball-outdoor-court', courtType: 'outdoor', courtName: 'Outdoor Court', courtCount: 3 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 2 },
    ]
  },
  {
    slug: 'grapevine', name: 'Grapevine, TX',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 7 },
      { typeSlug: 'pickleball-outdoor-court', courtType: 'outdoor', courtName: 'Outdoor Court', courtCount: 2 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 2 },
    ]
  },
  {
    slug: 'san-antonio', name: 'San Antonio, TX',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 4 },
      { typeSlug: 'pickleball-outdoor-court', courtType: 'outdoor', courtName: 'Outdoor Court', courtCount: 6 },
    ]
  },
  {
    slug: 'webster-tx', name: 'Webster, TX',
    courtTypes: [
      { typeSlug: 'pickleball-indoor-court', courtType: 'indoor', courtName: 'Indoor Court', courtCount: 6 },
      { typeSlug: 'pickleball-covered-court', courtType: 'outdoor', courtName: 'Covered Court', courtCount: 2 },
    ]
  },
];

type Step = 'form' | 'loading' | 'review' | 'saving' | 'success' | 'error';

export default function ChickenNPickleIntegration({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>('form');
  const [selectedSlug, setSelectedSlug] = useState('oklahoma-city');
  const [error, setError] = useState('');
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [editableCourts, setEditableCourts] = useState<Court[]>([]);

  const token = getAuthToken();
  const selectedLocation = CNP_LOCATIONS.find(l => l.slug === selectedSlug)!;

  const handlePreview = async () => {
    setError('');
    setStep('loading');

    try {
      const res = await fetch(`${API_URL}/api/integrations/chickennpickle/preview${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          locationSlug: selectedSlug,
          courtTypes: selectedLocation.courtTypes
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      const courts: Court[] = (data.data?.courts || []).map((c: any) => ({
        ...c,
        hourlyRateCents: c.type === 'outdoor' ? DEFAULT_OUTDOOR_RATE_CENTS : DEFAULT_INDOOR_RATE_CENTS
      }));

      setPreviewResult(data);
      setEditableCourts(courts);
      setStep('review');
    } catch (err: any) {
      setError(err.message || 'Preview failed');
      setStep('form');
    }
  };

  const handleCourtNameChange = (index: number, name: string) => {
    const updated = [...editableCourts];
    updated[index] = { ...updated[index], name };
    setEditableCourts(updated);
  };

  const handleCourtRateChange = (index: number, cents: number) => {
    const updated = [...editableCourts];
    updated[index] = { ...updated[index], hourlyRateCents: cents };
    setEditableCourts(updated);
  };

  const handleImport = async () => {
    setStep('saving');
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/integrations/import${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          integrationType: 'chickennpickle',
          externalId: selectedSlug,
          config: {
            locationSlug: selectedSlug,
            courtTypes: selectedLocation.courtTypes
          },
          courts: editableCourts,
          schedule: [],
          operatingHours: { open: null, close: null },
          rawAvailability: previewResult?.raw?.availability || []
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Import failed');
      setStep('review');
    }
  };

  if (step === 'success') {
    return (
      <div className="integration-setup">
        <div className="integration-setup__header">
          <button className="integration-setup__back" onClick={onClose}>← Back</button>
          <h2>Chicken N Pickle</h2>
        </div>
        <div className="integration-setup__success">
          <div className="integration-setup__success-icon">✓</div>
          <h3>Connected!</h3>
          <p>Chicken N Pickle — {selectedLocation.name} availability is now syncing.</p>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="integration-setup">
      <div className="integration-setup__header">
        <button className="integration-setup__back" onClick={onClose}>← Back</button>
        <h2>Chicken N Pickle</h2>
        <span className="integration-badge" style={{ background: '#6366f1', color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>Internal</span>
      </div>

      {step === 'form' || step === 'loading' ? (
        <div className="integration-setup__body">
          <p className="integration-setup__description">
            Connect a Chicken N Pickle location to sync real-time pickleball court availability.
            Uses their public reservation API — no credentials needed.
          </p>

          <div className="integration-setup__field">
            <label>Location</label>
            <select
              value={selectedSlug}
              onChange={e => setSelectedSlug(e.target.value)}
              disabled={step === 'loading'}
            >
              {CNP_LOCATIONS.map(loc => (
                <option key={loc.slug} value={loc.slug}>
                  {loc.name} ({loc.courtTypes.reduce((sum, ct) => sum + ct.courtCount, 0)} courts)
                </option>
              ))}
            </select>
          </div>

          <div className="integration-setup__field">
            <label>Court Types</label>
            <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              {selectedLocation.courtTypes.map(ct => (
                <div key={ct.typeSlug}>
                  {ct.courtName}: <strong>{ct.courtCount}</strong> ({ct.courtType})
                </div>
              ))}
            </div>
          </div>

          {error && <div className="integration-setup__error">{error}</div>}

          <div className="integration-setup__actions">
            <button className="btn btn-primary" onClick={handlePreview} disabled={step === 'loading'}>
              {step === 'loading' ? 'Loading...' : 'Preview'}
            </button>
            <button className="btn btn-secondary" onClick={onClose} disabled={step === 'loading'}>
              Cancel
            </button>
          </div>
        </div>
      ) : step === 'review' || step === 'saving' ? (
        <div className="integration-setup__body">
          <p className="integration-setup__description">
            Found <strong>{editableCourts.length} court{editableCourts.length !== 1 ? 's' : ''}</strong> at Chicken N Pickle — {selectedLocation.name}.
            Review and edit names and rates before connecting.
          </p>

          <div className="integration-setup__courts">
            {editableCourts.map((court, i) => (
              <div key={i} className="integration-setup__court-row">
                <input
                  type="text"
                  value={court.name}
                  onChange={e => handleCourtNameChange(i, e.target.value)}
                  className="integration-setup__court-name"
                  disabled={step === 'saving'}
                />
                <span className="integration-setup__court-type">{court.type}</span>
                <div className="integration-setup__court-rate">
                  <span>$</span>
                  <input
                    type="number"
                    value={court.hourlyRateCents / 100}
                    onChange={e => handleCourtRateChange(i, Math.round(parseFloat(e.target.value) * 100))}
                    min={0}
                    step={1}
                    className="integration-setup__rate-input"
                    disabled={step === 'saving'}
                  />
                  <span>/hr</span>
                </div>
              </div>
            ))}
          </div>

          {error && <div className="integration-setup__error">{error}</div>}

          <div className="integration-setup__actions">
            <button className="btn btn-primary" onClick={handleImport} disabled={step === 'saving'}>
              {step === 'saving' ? 'Connecting...' : 'Connect'}
            </button>
            <button className="btn btn-secondary" onClick={() => setStep('form')} disabled={step === 'saving'}>
              Back
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
